import React, { useState } from 'react';
import { StatsCard } from './shared/StatsCard';
import { Chart } from './shared/Chart';
import { Download, DollarSign, Droplet, Fish, Wallet, AlertCircle, Link, Camera, Heart, Gamepad2, Pill, Repeat, Zap, Globe, Theater, ArrowUpRight, ExternalLink, Info, BarChart, Settings, Brain } from 'lucide-react';
// User segments
const segments = [{
  id: 'whales',
  label: 'Whales',
  icon: <DollarSign size={16} className="text-emerald-500" data-id="element-10969" />,
  fallbackStrategy: 'none',
  routing: 'none'
}, {
  id: 'dolphins',
  label: 'Dolphins',
  icon: <Droplet size={16} className="text-blue-500" data-id="element-10970" />,
  fallbackStrategy: 'nutra-dating',
  routing: 'nutra-supplements'
}, {
  id: 'minnows',
  label: 'Minnows',
  icon: <Fish size={16} className="text-cyan-500" data-id="element-10971" />,
  fallbackStrategy: 'dating-games',
  routing: 'dating'
}, {
  id: 'non-spenders',
  label: 'Non-Spenders',
  icon: <Wallet size={16} className="text-gray-500" data-id="element-10972" />,
  fallbackStrategy: 'all-verticals',
  routing: 'dating'
}, {
  id: 'churn-risks',
  label: 'Churn Risks',
  icon: <AlertCircle size={16} className="text-red-500" data-id="element-10973" />,
  fallbackStrategy: 're-engagement',
  routing: 'cams'
}];
// Fallback methods
const fallbackMethods = [{
  id: 'lookalike',
  label: 'Lookalike Audiences',
  icon: <Repeat size={16} className="text-indigo-500" data-id="element-10974" />
}, {
  id: 'broad',
  label: 'Broad Targeting',
  icon: <Zap size={16} className="text-amber-500" data-id="element-10975" />
}, {
  id: 'context',
  label: 'Context Fallback',
  icon: <Globe size={16} className="text-blue-500" data-id="element-10976" />
}, {
  id: 'persona',
  label: 'Persona Fallback',
  icon: <Theater size={16} className="text-pink-500" data-id="element-10977" />
}];
// External verticals
const externalVerticals = [{
  id: 'cams',
  name: 'Live Cams',
  icon: <Camera size={16} className="text-pink-500" data-id="element-10978" />,
  revenue: 245000,
  clicks: 125000,
  conversions: 6250,
  ctr: 12.5,
  cvr: 5.0,
  rpm: 1.96,
  fallbackMethod: 'lookalike',
  algorithmCoverage: 85
}, {
  id: 'nutra',
  name: 'Nutra Supplements',
  icon: <Pill size={16} className="text-green-500" data-id="element-10979" />,
  revenue: 185000,
  clicks: 85000,
  conversions: 4250,
  ctr: 8.5,
  cvr: 5.0,
  rpm: 2.18,
  fallbackMethod: 'broad',
  algorithmCoverage: 72
}, {
  id: 'dating',
  name: 'Dating Sites',
  icon: <Heart size={16} className="text-red-500" data-id="element-10980" />,
  revenue: 320000,
  clicks: 180000,
  conversions: 9000,
  ctr: 18.0,
  cvr: 5.0,
  rpm: 1.78,
  fallbackMethod: 'context',
  algorithmCoverage: 78
}, {
  id: 'games',
  name: 'Adult Games',
  icon: <Gamepad2 size={16} className="text-purple-500" data-id="element-10981" />,
  revenue: 125000,
  clicks: 65000,
  conversions: 3250,
  ctr: 6.5,
  cvr: 5.0,
  rpm: 1.92,
  fallbackMethod: 'persona',
  algorithmCoverage: 65
}];
// Routing rules
const routingRules = [{
  id: 'none',
  label: 'No External Routing'
}, {
  id: 'cams',
  label: 'Live Cams'
}, {
  id: 'nutra-supplements',
  label: 'Nutra Supplements'
}, {
  id: 'dating',
  label: 'Dating Sites'
}, {
  id: 'games',
  label: 'Adult Games'
}, {
  id: 're-engagement',
  label: 'Re-engagement Flow'
}];
// Fallback strategies
const fallbackStrategies = [{
  id: 'none',
  label: 'No Fallback (100% Algorithm)'
}, {
  id: 'cams',
  label: 'Cams Only'
}, {
  id: 'nutra-dating',
  label: 'Nutra + Dating'
}, {
  id: 'dating-games',
  label: 'Dating + Games'
}, {
  id: 'all-verticals',
  label: 'All External Verticals'
}, {
  id: 're-engagement',
  label: 'Re-engagement First'
}];
// Smartlinks
const smartlinks = [{
  id: 'cam-link',
  name: 'Premium Cam Show',
  vertical: 'cams',
  url: 'https://example.com/cams/premium',
  payout: 45.0,
  clicks: 28500,
  conversions: 1425,
  revenue: 64125,
  algorithmCoverage: 82,
  fallbackMethod: 'lookalike'
}, {
  id: 'dating-link',
  name: 'Local Dating',
  vertical: 'dating',
  url: 'https://example.com/dating/local',
  payout: 35.0,
  clicks: 42000,
  conversions: 2100,
  revenue: 73500,
  algorithmCoverage: 75,
  fallbackMethod: 'context'
}, {
  id: 'nutra-link',
  name: 'Male Enhancement',
  vertical: 'nutra',
  url: 'https://example.com/supplements/enhancement',
  payout: 55.0,
  clicks: 18500,
  conversions: 925,
  revenue: 50875,
  algorithmCoverage: 68,
  fallbackMethod: 'broad'
}, {
  id: 'game-link',
  name: 'Fantasy Simulator',
  vertical: 'games',
  url: 'https://example.com/games/fantasy',
  payout: 25.0,
  clicks: 32500,
  conversions: 1625,
  revenue: 40625,
  algorithmCoverage: 70,
  fallbackMethod: 'persona'
}];
// Chart data for revenue by vertical
const revenueByVerticalData = [{
  name: 'Cams',
  value: externalVerticals.find(v => v.id === 'cams')?.revenue || 0
}, {
  name: 'Dating',
  value: externalVerticals.find(v => v.id === 'dating')?.revenue || 0
}, {
  name: 'Nutra',
  value: externalVerticals.find(v => v.id === 'nutra')?.revenue || 0
}, {
  name: 'Games',
  value: externalVerticals.find(v => v.id === 'games')?.revenue || 0
}];
// Chart data for algorithm coverage
const algorithmCoverageData = [{
  name: 'Cams',
  algorithm: externalVerticals.find(v => v.id === 'cams')?.algorithmCoverage || 0,
  fallback: 100 - (externalVerticals.find(v => v.id === 'cams')?.algorithmCoverage || 0)
}, {
  name: 'Dating',
  algorithm: externalVerticals.find(v => v.id === 'dating')?.algorithmCoverage || 0,
  fallback: 100 - (externalVerticals.find(v => v.id === 'dating')?.algorithmCoverage || 0)
}, {
  name: 'Nutra',
  algorithm: externalVerticals.find(v => v.id === 'nutra')?.algorithmCoverage || 0,
  fallback: 100 - (externalVerticals.find(v => v.id === 'nutra')?.algorithmCoverage || 0)
}, {
  name: 'Games',
  algorithm: externalVerticals.find(v => v.id === 'games')?.algorithmCoverage || 0,
  fallback: 100 - (externalVerticals.find(v => v.id === 'games')?.algorithmCoverage || 0)
}];
export const FallbackMonetization: React.FC = () => {
  const [segmentRouting, setSegmentRouting] = useState(segments.reduce((acc, segment) => ({
    ...acc,
    [segment.id]: segment.routing
  }), {}));
  const [segmentStrategy, setSegmentStrategy] = useState(segments.reduce((acc, segment) => ({
    ...acc,
    [segment.id]: segment.fallbackStrategy
  }), {}));
  const [smartlinkFallbacks, setSmartlinkFallbacks] = useState(smartlinks.reduce((acc, link) => ({
    ...acc,
    [link.id]: link.fallbackMethod
  }), {}));
  const handleRoutingChange = (segmentId: string, routing: string) => {
    setSegmentRouting({
      ...segmentRouting,
      [segmentId]: routing
    });
  };
  const handleStrategyChange = (segmentId: string, strategy: string) => {
    setSegmentStrategy({
      ...segmentStrategy,
      [segmentId]: strategy
    });
  };
  const handleFallbackMethodChange = (linkId: string, method: string) => {
    setSmartlinkFallbacks({
      ...smartlinkFallbacks,
      [linkId]: method
    });
  };
  const totalRevenue = externalVerticals.reduce((sum, vertical) => sum + vertical.revenue, 0);
  const totalClicks = externalVerticals.reduce((sum, vertical) => sum + vertical.clicks, 0);
  const totalConversions = externalVerticals.reduce((sum, vertical) => sum + vertical.conversions, 0);
  const avgCTR = externalVerticals.reduce((sum, vertical) => sum + vertical.ctr, 0) / externalVerticals.length;
  const avgCVR = externalVerticals.reduce((sum, vertical) => sum + vertical.cvr, 0) / externalVerticals.length;
  const avgRPM = externalVerticals.reduce((sum, vertical) => sum + vertical.rpm, 0) / externalVerticals.length;
  const avgAlgorithmCoverage = externalVerticals.reduce((sum, vertical) => sum + vertical.algorithmCoverage, 0) / externalVerticals.length;
  return <div data-id="element-10982">
      <div className="flex items-center justify-between mb-6" data-id="element-10983">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-10984">
          Fallback Monetization
        </h2>
        <div className="flex items-center gap-3" data-id="element-10985">
          <button className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-10986">
            <Download size={16} data-id="element-10987" />
            <span data-id="element-10988">Export Data</span>
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-10989">
        <StatsCard title="External Revenue" value={`$${(totalRevenue / 1000).toFixed(1)}K`} change={15.3} changeLabel="vs last month" icon={<ExternalLink size={16} className="text-blue-500" data-id="element-10991" />} className="bg-gradient-to-br from-white to-blue-50 dark:from-gray-800 dark:to-blue-900/20" data-id="element-10990" />
        <StatsCard title="Total Clicks" value={totalClicks.toLocaleString()} change={8.7} changeLabel="vs last month" icon={<ArrowUpRight size={16} className="text-amber-500" data-id="element-10993" />} data-id="element-10992" />
        <StatsCard title="Conversions" value={totalConversions.toLocaleString()} change={12.2} changeLabel="vs last month" icon={<BarChart size={16} className="text-green-500" data-id="element-10995" />} data-id="element-10994" />
        <StatsCard title="Algorithm Coverage" value={`${avgAlgorithmCoverage.toFixed(1)}%`} change={4.5} changeLabel="vs last month" icon={<Settings size={16} className="text-purple-500" data-id="element-10997" />} className="bg-gradient-to-br from-white to-purple-50 dark:from-gray-800 dark:to-purple-900/20" data-id="element-10996" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6" data-id="element-10998">
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-10999">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-11000">
            <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-11001">
              <Link size={18} className="text-blue-500 mr-2" data-id="element-11002" />
              Smartlink System
            </h3>
          </div>
          <div className="overflow-x-auto" data-id="element-11003">
            <table className="w-full" data-id="element-11004">
              <thead data-id="element-11005">
                <tr className="bg-gray-50 dark:bg-gray-900/30" data-id="element-11006">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-11007">
                    Smartlink
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-11008">
                    Vertical
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-11009">
                    Payout
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-11010">
                    Clicks
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-11011">
                    Conversions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-11012">
                    Revenue
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-11013">
                    Algorithm Coverage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-11014">
                    Fallback Method
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-11015">
                {smartlinks.map(link => <tr key={link.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60" data-id="element-11016">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-11017">
                      <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-11018">
                        {link.name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[150px]" data-id="element-11019">
                        {link.url}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-11020">
                      <div className="flex items-center" data-id="element-11021">
                        {link.vertical === 'cams' ? <Camera size={16} className="text-pink-500 mr-2" data-id="element-11022" /> : link.vertical === 'dating' ? <Heart size={16} className="text-red-500 mr-2" data-id="element-11023" /> : link.vertical === 'nutra' ? <Pill size={16} className="text-green-500 mr-2" data-id="element-11024" /> : <Gamepad2 size={16} className="text-purple-500 mr-2" data-id="element-11025" />}
                        <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-11026">
                          {link.vertical.charAt(0).toUpperCase() + link.vertical.slice(1)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-11027">
                      ${link.payout.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-11028">
                      {link.clicks.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-11029">
                      {link.conversions.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600 dark:text-green-400" data-id="element-11030">
                      ${link.revenue.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-11031">
                      <div className="flex items-center" data-id="element-11032">
                        <div className="w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-11033">
                          <div className="h-full bg-purple-500 rounded-full" style={{
                        width: `${link.algorithmCoverage}%`
                      }} data-id="element-11034"></div>
                        </div>
                        <span className="ml-2" data-id="element-11035">{link.algorithmCoverage}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-11036">
                      <select value={smartlinkFallbacks[link.id]} onChange={e => handleFallbackMethodChange(link.id, e.target.value)} className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:focus:ring-purple-400 dark:focus:border-purple-400" data-id="element-11037">
                        {fallbackMethods.map(method => <option key={method.id} value={method.id} data-id="element-11038">
                            {method.label}
                          </option>)}
                      </select>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
        <div data-id="element-11039">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6" data-id="element-11040">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-11041">
              <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-11042">
                <BarChart size={18} className="text-green-500 mr-2" data-id="element-11043" />
                Revenue by Vertical
              </h3>
            </div>
            <div className="p-6" data-id="element-11044">
              <div className="h-64" data-id="element-11045">
                <Chart title="" data={revenueByVerticalData} type="pie" height={250} data-id="element-11046" />
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-11047">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-11048">
              <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-11049">
                <Info size={18} className="text-blue-500 mr-2" data-id="element-11050" />
                Performance Metrics
              </h3>
            </div>
            <div className="p-4" data-id="element-11051">
              <div className="space-y-4" data-id="element-11052">
                <div className="flex justify-between items-center" data-id="element-11053">
                  <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-11054">
                    Average CTR:
                  </div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-11055">
                    {avgCTR.toFixed(1)}%
                  </div>
                </div>
                <div className="flex justify-between items-center" data-id="element-11056">
                  <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-11057">
                    Average CVR:
                  </div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-11058">
                    {avgCVR.toFixed(1)}%
                  </div>
                </div>
                <div className="flex justify-between items-center" data-id="element-11059">
                  <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-11060">
                    Average RPM:
                  </div>
                  <div className="text-sm font-medium text-green-600 dark:text-green-400" data-id="element-11061">
                    ${avgRPM.toFixed(2)}
                  </div>
                </div>
                <div className="flex justify-between items-center" data-id="element-11062">
                  <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-11063">
                    Algorithm vs Fallback Lift:
                  </div>
                  <div className="text-sm font-medium text-green-600 dark:text-green-400" data-id="element-11064">
                    +38.5%
                  </div>
                </div>
                <div className="h-[1px] bg-gray-200 dark:bg-gray-700 my-2" data-id="element-11065"></div>
                <div className="flex justify-between items-center" data-id="element-11066">
                  <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-11067">
                    Top Performing Vertical:
                  </div>
                  <div className="flex items-center text-sm font-medium text-gray-900 dark:text-white" data-id="element-11068">
                    <Heart size={14} className="text-red-500 mr-1" data-id="element-11069" />
                    Dating Sites
                  </div>
                </div>
                <div className="flex justify-between items-center" data-id="element-11070">
                  <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-11071">
                    Best Fallback Method:
                  </div>
                  <div className="flex items-center text-sm font-medium text-gray-900 dark:text-white" data-id="element-11072">
                    <Repeat size={14} className="text-indigo-500 mr-1" data-id="element-11073" />
                    Lookalike Audiences
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6" data-id="element-11074">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-11075">
          <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-11076">
            <Settings size={18} className="text-gray-500 dark:text-gray-400 mr-2" data-id="element-11077" />
            Routing Rules by Segment
          </h3>
        </div>
        <div className="overflow-x-auto" data-id="element-11078">
          <table className="w-full" data-id="element-11079">
            <thead data-id="element-11080">
              <tr className="bg-gray-50 dark:bg-gray-900/30" data-id="element-11081">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-11082">
                  Segment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-11083">
                  Fallback Strategy
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-11084">
                  Primary Routing
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-11085">
              {segments.map(segment => <tr key={segment.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60" data-id="element-11086">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-11087">
                    <div className="flex items-center" data-id="element-11088">
                      <div className="flex-shrink-0" data-id="element-11089">
                        <div className="w-8 h-8 rounded-md bg-gray-100 dark:bg-gray-700 flex items-center justify-center" data-id="element-11090">
                          {segment.icon}
                        </div>
                      </div>
                      <div className="ml-3" data-id="element-11091">
                        <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-11092">
                          {segment.label}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-11093">
                    <select value={segmentStrategy[segment.id]} onChange={e => handleStrategyChange(segment.id, e.target.value)} className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:focus:ring-purple-400 dark:focus:border-purple-400" data-id="element-11094">
                      {fallbackStrategies.map(strategy => <option key={strategy.id} value={strategy.id} data-id="element-11095">
                          {strategy.label}
                        </option>)}
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-11096">
                    <select value={segmentRouting[segment.id]} onChange={e => handleRoutingChange(segment.id, e.target.value)} className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:focus:ring-purple-400 dark:focus:border-purple-400" data-id="element-11097">
                      {routingRules.map(rule => <option key={rule.id} value={rule.id} data-id="element-11098">
                          {rule.label}
                        </option>)}
                    </select>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-id="element-11099">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-11100">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-11101">
            <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-11102">
              <Settings size={18} className="text-purple-500 mr-2" data-id="element-11103" />
              Algorithm vs Fallback Coverage
            </h3>
          </div>
          <div className="p-6" data-id="element-11104">
            <div className="h-80" data-id="element-11105">
              <Chart title="" data={algorithmCoverageData} type="bar" height={300} yAxisLabel="Coverage (%)" data-id="element-11106" />
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-11107">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-11108">
            <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-11109">
              <Info size={18} className="text-blue-500 mr-2" data-id="element-11110" />
              Fallback Monetization Logic
            </h3>
          </div>
          <div className="p-6" data-id="element-11111">
            <div className="space-y-6" data-id="element-11112">
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4" data-id="element-11113">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center" data-id="element-11114">
                  <Settings size={16} className="text-blue-500 mr-2" data-id="element-11115" />
                  Monetization Decision Flow
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4" data-id="element-11116">
                  When a user can't be monetized within SinShift, the system
                  follows this decision flow:
                </p>
                <div className="space-y-3" data-id="element-11117">
                  <div className="flex items-start" data-id="element-11118">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" data-id="element-11119">
                      1
                    </div>
                    <div className="ml-3" data-id="element-11120">
                      <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-11121">
                        <strong data-id="element-11122">Check Segment:</strong> Identify user segment
                        (Whale, Dolphin, etc.)
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-11123">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" data-id="element-11124">
                      2
                    </div>
                    <div className="ml-3" data-id="element-11125">
                      <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-11126">
                        <strong data-id="element-11127">Apply Segment Strategy:</strong> Use configured
                        fallback strategy for that segment
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-11128">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" data-id="element-11129">
                      3
                    </div>
                    <div className="ml-3" data-id="element-11130">
                      <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-11131">
                        <strong data-id="element-11132">Determine Routing:</strong> Send to primary
                        routing destination
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-11133">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" data-id="element-11134">
                      4
                    </div>
                    <div className="ml-3" data-id="element-11135">
                      <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-11136">
                        <strong data-id="element-11137">Apply Fallback Method:</strong> Use configured
                        targeting method (Lookalike, Broad, etc.)
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4" data-id="element-11138">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center" data-id="element-11139">
                  <Brain size={16} className="text-purple-500 mr-2" data-id="element-11140" />
                  Algorithmic Routing Signals
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2" data-id="element-11141">
                  The system analyzes these signals to determine the optimal
                  external routing:
                </p>
                <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400 list-disc pl-5" data-id="element-11142">
                  <li data-id="element-11143">Chat content analysis (keywords, topics)</li>
                  <li data-id="element-11144">Previous external link engagement</li>
                  <li data-id="element-11145">Similar user conversion patterns</li>
                  <li data-id="element-11146">Time of day and day of week</li>
                  <li data-id="element-11147">Device and location context</li>
                </ul>
              </div>
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4" data-id="element-11148">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center" data-id="element-11149">
                  <AlertCircle size={16} className="text-amber-500 mr-2" data-id="element-11150" />
                  Segment-Specific Logic
                </h4>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400" data-id="element-11151">
                  <li className="flex items-center" data-id="element-11152">
                    <DollarSign size={14} className="text-emerald-500 mr-2 flex-shrink-0" data-id="element-11153" />
                    <span data-id="element-11154">
                      <strong data-id="element-11155">Whales:</strong> Rarely routed externally (focus
                      on retention)
                    </span>
                  </li>
                  <li className="flex items-center" data-id="element-11156">
                    <Droplet size={14} className="text-blue-500 mr-2 flex-shrink-0" data-id="element-11157" />
                    <span data-id="element-11158">
                      <strong data-id="element-11159">Dolphins:</strong> Nutra offers show highest
                      conversion
                    </span>
                  </li>
                  <li className="flex items-center" data-id="element-11160">
                    <Fish size={14} className="text-cyan-500 mr-2 flex-shrink-0" data-id="element-11161" />
                    <span data-id="element-11162">
                      <strong data-id="element-11163">Minnows:</strong> Dating offers perform best
                    </span>
                  </li>
                  <li className="flex items-center" data-id="element-11164">
                    <Wallet size={14} className="text-gray-500 mr-2 flex-shrink-0" data-id="element-11165" />
                    <span data-id="element-11166">
                      <strong data-id="element-11167">Non-Spenders:</strong> Dating and games for
                      acquisition
                    </span>
                  </li>
                  <li className="flex items-center" data-id="element-11168">
                    <AlertCircle size={14} className="text-red-500 mr-2 flex-shrink-0" data-id="element-11169" />
                    <span data-id="element-11170">
                      <strong data-id="element-11171">Churn Risks:</strong> Cams and re-engagement focus
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};